import { useEffect, useRef, useState } from 'react';
import useSWR, { SWRResponse } from 'swr';
import type { SearchProductsResponseBody } from '@/api/catalog-v2/search/products';

interface UseSearchSuggestionsKey {
  q: string;
  limit?: number;
}

const DEBOUNCE_MS = 200;
const DEFAULT_LIMIT = 6;

export function useSearchSuggestions(
  key: UseSearchSuggestionsKey | null,
): SWRResponse<SearchProductsResponseBody, Error> {
  const abortRef = useRef<AbortController | null>(null);
  const [debouncedQ, setDebouncedQ] = useState(key?.q?.trim() ?? '');

  useEffect(() => {
    const q = key?.q?.trim() ?? '';
    const timer = setTimeout(() => setDebouncedQ(q), DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [key?.q]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const swrKey =
    debouncedQ.length > 0 && key
      ? ([
          '/api/catalog-v2/search/products',
          debouncedQ,
          key.limit ?? DEFAULT_LIMIT,
        ] as const)
      : null;

  return useSWR<SearchProductsResponseBody, Error>(
    swrKey,
    async (url: string, q: string, limit: number) => {
      abortRef.current?.abort();
      const controller = new AbortController();

      abortRef.current = controller;

      const params = new URLSearchParams({
        q,
        limit: String(limit),
        includeFilters: 'false',
      });
      const res = await fetch(`${url}?${params}`, {
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`products fetch failed: ${res.status}`);
      }

      return res.json() as Promise<SearchProductsResponseBody>;
    },
    {
      shouldRetryOnError: (err: Error) => err.name !== 'AbortError',
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
}
